import React, { createContext, useCallback, useMemo, useRef, useState } from "react"

export type RequiredItemDetailsModalState = {
  isOpen: boolean
  requiredNote: string
}

const detailsModalInitialState: RequiredItemDetailsModalState = {
  isOpen: false,
  requiredNote: ""
}

export type RequiredItemModalsContextValue = {
  confirmRemovalIsOpen: boolean
  openConfirmRemovalModal: (onConfirm: () => void) => void
  closeConfirmRemovalModal: () => void
  onConfirmRemoval: () => void
  detailsModalState: RequiredItemDetailsModalState
  openRequiredItemDetails: (requiredNote: string) => void
  closeRequiredItemDetails: () => void
}

export const RequiredItemModalsContext = createContext<RequiredItemModalsContextValue | null>(null)

export function RequiredItemModalsProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [confirmRemovalIsOpen, setConfirmRemovalIsOpen] = useState(false)
  const onConfirmRef = useRef<(() => void) | null>(null)
  const [detailsModalState, setDetailsModalState] = useState<RequiredItemDetailsModalState>(detailsModalInitialState)

  const openConfirmRemovalModal = useCallback((onConfirm: () => void) => {
    onConfirmRef.current = onConfirm
    setConfirmRemovalIsOpen(true)
  }, [])

  const closeConfirmRemovalModal = useCallback(() => {
    onConfirmRef.current = null
    setConfirmRemovalIsOpen(false)
  }, [])

  const onConfirmRemoval = useCallback(() => {
    onConfirmRef.current?.()
    onConfirmRef.current = null
    setConfirmRemovalIsOpen(false)
  }, [])

  const openRequiredItemDetails = useCallback((requiredNote: string) => {
    setDetailsModalState({ isOpen: true, requiredNote })
  }, [])

  const closeRequiredItemDetails = useCallback(() => {
    setDetailsModalState(detailsModalInitialState)
  }, [])

  const value = useMemo<RequiredItemModalsContextValue>(
    () => ({
      confirmRemovalIsOpen,
      openConfirmRemovalModal,
      closeConfirmRemovalModal,
      onConfirmRemoval,
      detailsModalState,
      openRequiredItemDetails,
      closeRequiredItemDetails
    }),
    [
      confirmRemovalIsOpen,
      detailsModalState,
      openConfirmRemovalModal,
      closeConfirmRemovalModal,
      onConfirmRemoval,
      openRequiredItemDetails,
      closeRequiredItemDetails
    ]
  )

  return <RequiredItemModalsContext.Provider value={value}>{children}</RequiredItemModalsContext.Provider>
}
